import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnDestroy,
	Output,
	ViewChild,
} from '@angular/core';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';

import {
	DxDataGridModule,
	DxTreeViewModule,
} from 'devextreme-angular';
import {
	DxoColumnChooserModule,
	DxoFilterRowModule,
	DxoSearchPanelModule,
} from 'devextreme-angular/ui/nested';


import { PathNode, ResponseTab, Vm } from '../../state/integration-tester.models';
import { TrustHtmlPipe } from '../../../pipes/trust_html.pipe';
import { PrettyXmlPipe } from '../../../pipes/pretty_xml.pipe';

type FindState = {
	html: string;
	matches: number;
	index: number;
	lineCount: number;
	kind: 'json' | 'xml' | 'text';

	// NEW:
	isHuge: boolean;
	rawText: string; // the pretty text for small, raw for huge
};

@Component({
	selector: 'it-response-pane',
	standalone: true,
	templateUrl: './it-response-pane.component.html',
	styleUrls: ['./it-response-pane.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgIf,
		NgFor,
		AsyncPipe,
		TrustHtmlPipe,
		PrettyXmlPipe,
		DxDataGridModule,
		DxTreeViewModule,
		DxoFilterRowModule,
		DxoSearchPanelModule,
		DxoColumnChooserModule,
		ScrollingModule
	],
})
export class ItResponsePaneComponent implements AfterViewInit, OnDestroy {
	@Input({ required: true }) vm!: Vm;

	// Observable<{ html, matches, index }>
	@Input({ required: true }) renderedBodyHtml$!: Observable<FindState>;

	@Input({ required: true }) pathTreeItems$!: Observable<PathNode[]>;

	@Input() findQuery = '';

	@Output() setResponseTab = new EventEmitter<ResponseTab>();
	@Output() copyBody = new EventEmitter<void>();

	@Output() findChanged = new EventEmitter<string>();
	@Output() setFindIndex = new EventEmitter<number>();
	@Output() clearFind = new EventEmitter<void>();

	@Output() gridPathChanged = new EventEmitter<string>();

	@Output() clearResponseCache = new EventEmitter<void>();

	@ViewChild('respLn', { static: false }) respLn?: ElementRef<HTMLElement>;

	@ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;

	isHuge = false;
	rawText = '';
	lineStarts: number[] = [0]; // start index of each line

	// used by virtual scroll template
	get lineCount(): number {
		return Math.max(1, this.lineStarts.length);
	}

	getLine(i: number): string {
		const start = this.lineStarts[i] ?? 0;
		const end = (i + 1 < this.lineStarts.length) ? this.lineStarts[i + 1] - 1 : this.rawText.length;
		return this.rawText.slice(start, Math.max(start, end));
	}

	private buildLineIndex(text: string): number[] {
		const starts: number[] = [0];
		for (let i = 0; i < text.length; i++) {
			if (text.charCodeAt(i) === 10) starts.push(i + 1); // '\n'
		}
		return starts;
	}

	canCopy = false;
	copied = false;

	respLineNumbers: number[] = [];
	lastKind: 'json' | 'xml' | 'text' = 'text';

	readonly VIRT_LINE_HEIGHT = 18;

	lineIndexArray: number[] = [];


	trackByIndex = (i: number) => i;

	syncRespScroll(): void {
		if (this.isHuge) return;
		const code = this.respPretty?.nativeElement;
		const ln = this.respLn?.nativeElement;
		if (!code || !ln) return;
		ln.scrollTop = code.scrollTop;
	}


	isPathPickerOpen = false;

	@ViewChild('respPretty', { static: false }) respPretty?: ElementRef<HTMLElement>;
	@ViewChild('findInput', { static: false }) findInput?: ElementRef<HTMLInputElement>;

	private readonly destroy$ = new Subject<void>();
	private lastFindState: FindState = {
		html: '',
		matches: 0,
		index: 0,
		lineCount: 1,
		kind: 'text',
		isHuge: false,
		rawText: '',
	};

	private buildLineStarts(text: string): number[] {
		const starts: number[] = [0];
		for (let i = 0; i < text.length; i++) {
			if (text.charCodeAt(i) === 10) starts.push(i + 1); // '\n'
		}
		return starts;
	}


	togglePathPicker(): void { this.isPathPickerOpen = !this.isPathPickerOpen; }

	ngAfterViewInit(): void {
		this.renderedBodyHtml$
			.pipe(takeUntil(this.destroy$))
			.subscribe(state => {
				this.lastFindState = state as any;
				this.lastKind = (state as any).kind ?? 'text';

				// NEW
				this.isHuge = !!(state as any).isHuge;
				this.rawText = ((state as any).rawText ?? '').toString();

				if (this.isHuge) {
					this.lineStarts = this.buildLineStarts(this.rawText);
					this.lineIndexArray = Array.from({ length: this.lineStarts.length }, (_, i) => i);
					this.canCopy = this.rawText.trim().length > 0;
					return; // huge mode doesn't use html/respLineNumbers
				}

				// existing normal-mode behavior:
				const lc = (state as any).lineCount ?? 1;
				this.respLineNumbers = Array.from({ length: lc }, (_, i) => i + 1);
				this.canCopy = !!(state?.html && state.html.trim().length > 0);
				this.scrollToActiveSoon();
			});
	}

	isClearingCache = false;

	onClearCacheClick(): void {
		if (this.isClearingCache) return;

		this.isClearingCache = true;

		// clear find + close path picker immediately
		this.clearFind.emit();
		this.isPathPickerOpen = false;

		// reset to Preview tab (optional, but avoids being stuck on disabled Headers/Grid)
		this.setResponseTab.emit('body');

		// ask parent/facade to clear localStorage cache + also clear the current response
		this.clearResponseCache.emit();

		setTimeout(() => (this.isClearingCache = false), 900);
	}

	onFindKeyDown(e: KeyboardEvent): void {
		if (e.key !== 'Enter') return;

		// Prevent form submits / default behavior
		e.preventDefault();
		e.stopPropagation();

		if (!this.canFind()) return;

		// Shift+Enter => Prev, Enter => Next
		if (e.shiftKey) this.onPrev();
		else this.onNext();
	}

	async onCopyClick(): Promise<void> {
		if (!this.canCopy) return;

		// Prefer copying the original bodyText (what user expects),
		// but fall back to extracted text from the rendered preview.
		const raw = (this.vm?.lastResponse?.bodyText ?? '').toString();
		const text = raw.length ? raw : (this.respPretty?.nativeElement?.innerText ?? '');

		try {
			await navigator.clipboard.writeText(text);
			this.copied = true;
			setTimeout(() => (this.copied = false), 800);

			// Keep your existing output for compatibility (optional)
			this.copyBody.emit();
		} catch {
			// Fallback for older browsers / blocked clipboard API
			const ta = document.createElement('textarea');
			ta.value = text;
			ta.style.position = 'fixed';
			ta.style.opacity = '0';
			document.body.appendChild(ta);
			ta.focus();
			ta.select();
			try {
				document.execCommand('copy');
				this.copied = true;
				setTimeout(() => (this.copied = false), 800);
				this.copyBody.emit();
			} finally {
				document.body.removeChild(ta);
			}
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// Ctrl/Cmd+F focuses the find box when Preview is visible
	@HostListener('document:keydown', ['$event'])
	onDocKeyDown(e: KeyboardEvent): void {
		const isMac = navigator.platform.toLowerCase().includes('mac');
		const mod = isMac ? e.metaKey : e.ctrlKey;

		if (!mod) return;
		if (e.key.toLowerCase() !== 'f') return;

		if (this.vm?.responseTab !== 'body') return;
		if (!this.vm?.lastResponse) return;

		e.preventDefault();
		setTimeout(() => this.findInput?.nativeElement?.focus(), 0);
	}

	canFind(): boolean {
		const q = (this.findQuery ?? '').trim();
		return q.length >= 2 && (this.lastFindState?.matches ?? 0) > 0;
	}

	onPrev(): void {
		if (!this.canFind()) return;
		const { matches, index } = this.lastFindState;
		const next = (index - 1 + matches) % matches;
		this.setFindIndex.emit(next);
	}

	onNext(): void {
		if (!this.canFind()) return;
		const { matches, index } = this.lastFindState;
		const next = (index + 1) % matches;
		this.setFindIndex.emit(next);
	}

	private scrollToActiveSoon(): void {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => this.scrollToActiveMark());
		});
	}

	private scrollToActiveMark(): void {
		const host = this.respPretty?.nativeElement;
		if (!host) return;

		const active = host.querySelector<HTMLElement>('mark.it-mark.is-active');
		if (!active) return;

		// ensure the right container scrolls
		const scroller = host.closest<HTMLElement>('.it-resbody') ?? host;
		const a = active.getBoundingClientRect();
		const s = scroller.getBoundingClientRect();

		scroller.scrollTop += (a.top - s.top) - (s.height / 2);
	}

	setResponseTabLocal(tab: ResponseTab): void {
		this.setResponseTab.emit(tab);
		if (tab !== 'grid') this.isPathPickerOpen = false;
	}

	onPathNodeClick(e: any): void {
		const node = e?.itemData as PathNode | undefined;
		if (!node) return;
		this.gridPathChanged.emit(node.path);
		this.setResponseTabLocal('grid');
	}

	isXmlLikeResponse(): boolean {
		const t = (this.vm?.lastResponse?.bodyText ?? '').toString().trim();
		return t.startsWith('<') && t.endsWith('>');
	}

	canGrid(): boolean {
		const t = (this.vm?.lastResponse?.bodyText ?? '').toString().trim();
		return (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']')) || (t.startsWith('<') && t.endsWith('>'));
	}
}
